/**
 * Personalização visual com uma trava: a barbearia escolhe a cor, mas não
 * consegue fabricar um app ilegível.
 *
 * O barbeiro vai escolher a cor pelo gosto, no celular, provavelmente no escuro
 * da loja — e o mesmo tom precisa funcionar no tema claro e no escuro. Então a
 * cor dele é respeitada onde dá, e ajustada onde o contraste não fecha.
 */

export interface Rgb {
  r: number;
  g: number;
  b: number;
}

/** Mínimo do WCAG AA para texto normal. */
export const MIN_TEXT_CONTRAST = 4.5;
/** Mínimo do WCAG AA para texto grande e elementos de interface. */
export const MIN_UI_CONTRAST = 3;

export const LIGHT_SURFACE = '#FFFFFF';
export const DARK_SURFACE = '#16161A';

const HEX_RE = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i;

export function parseHex(hex: string): Rgb | null {
  const match = HEX_RE.exec(hex.trim());
  if (!match) return null;

  let value = match[1] as string;
  if (value.length === 3) {
    value = value
      .split('')
      .map((char) => char + char)
      .join('');
  }

  return {
    r: Number.parseInt(value.slice(0, 2), 16),
    g: Number.parseInt(value.slice(2, 4), 16),
    b: Number.parseInt(value.slice(4, 6), 16),
  };
}

export function isValidHex(hex: string): boolean {
  return parseHex(hex) !== null;
}

function clampChannel(value: number): number {
  return Math.max(0, Math.min(255, Math.round(value)));
}

export function toHex({ r, g, b }: Rgb): string {
  const channel = (value: number) => clampChannel(value).toString(16).padStart(2, '0');
  return `#${channel(r)}${channel(g)}${channel(b)}`.toUpperCase();
}

function channelLuminance(value: number): number {
  const channel = value / 255;
  return channel <= 0.04045 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4;
}

export function relativeLuminance(color: Rgb): number {
  return (
    0.2126 * channelLuminance(color.r) +
    0.7152 * channelLuminance(color.g) +
    0.0722 * channelLuminance(color.b)
  );
}

/** Razão de contraste do WCAG: 1 (igual) a 21 (preto sobre branco). */
export function contrastRatio(a: string, b: string): number {
  const colorA = parseHex(a);
  const colorB = parseHex(b);
  if (!colorA || !colorB) return 1;

  const lumA = relativeLuminance(colorA);
  const lumB = relativeLuminance(colorB);
  const lighter = Math.max(lumA, lumB);
  const darker = Math.min(lumA, lumB);

  return (lighter + 0.05) / (darker + 0.05);
}

export function mix(from: string, to: string, weight: number): string {
  const colorA = parseHex(from);
  const colorB = parseHex(to);
  if (!colorA || !colorB) return from;

  const ratio = Math.max(0, Math.min(1, weight));
  return toHex({
    r: colorA.r + (colorB.r - colorA.r) * ratio,
    g: colorA.g + (colorB.g - colorA.g) * ratio,
    b: colorA.b + (colorB.b - colorA.b) * ratio,
  });
}

export function lighten(color: string, amount: number): string {
  return mix(color, '#FFFFFF', amount);
}

export function darken(color: string, amount: number): string {
  return mix(color, '#000000', amount);
}

/**
 * Texto que se lê por cima desta cor. Não é "claro ou escuro por regra de bolso":
 * calculamos os dois contrastes e devolvemos o melhor.
 */
export function readableForeground(background: string): string {
  const onDark = contrastRatio(background, '#FFFFFF');
  const onLight = contrastRatio(background, '#101014');
  return onDark >= onLight ? '#FFFFFF' : '#101014';
}

/**
 * Aproxima a cor do contraste mínimo contra um fundo, clareando ou escurecendo
 * aos poucos. Devolve a cor original quando ela já passa.
 *
 * É o que salva o dourado da barbearia no tema claro: bonito no escuro,
 * ilegível no branco, então clareia/escurece só o necessário.
 */
export function ensureContrast(
  color: string,
  background: string,
  minRatio = MIN_UI_CONTRAST,
): string {
  if (!isValidHex(color) || !isValidHex(background)) return color;
  if (contrastRatio(color, background) >= minRatio) return color;

  const backgroundIsDark = relativeLuminance(parseHex(background) as Rgb) < 0.5;
  const target = backgroundIsDark ? '#FFFFFF' : '#000000';

  let adjusted = color;
  for (let step = 1; step <= 20; step += 1) {
    adjusted = mix(color, target, step * 0.05);
    if (contrastRatio(adjusted, background) >= minRatio) return adjusted;
  }

  return adjusted;
}

export interface ThemeColors {
  primary: string;
  primaryForeground: string;
  primaryHover: string;
  accent: string;
  accentForeground: string;
}

export interface TenantTheme {
  light: ThemeColors;
  dark: ThemeColors;
}

export interface Palette {
  id: string;
  name: string;
  primary: string;
  accent: string;
}

/**
 * Combinações prontas, para o barbeiro resolver a identidade da loja em um
 * toque, em pé, entre dois cortes.
 *
 * Existir esta lista não impede escolher qualquer cor — impede só que a escolha
 * comece do zero, que é onde quase todo mundo acerta menos do que o padrão. As
 * seis passam pelo `ensureContrast` sem serem alteradas nos dois temas, o que
 * significa que a cor que o dono vê na prévia é a cor que o app usa.
 */
export const PALETTES: readonly Palette[] = [
  { id: 'classica', name: 'Clássica', primary: '#C9A227', accent: '#0E5A6B' },
  { id: 'navalha', name: 'Navalha', primary: '#B03A2E', accent: '#2C3E50' },
  { id: 'menta', name: 'Menta', primary: '#1E9E7E', accent: '#3D5A80' },
  { id: 'noturna', name: 'Noturna', primary: '#7B5CD6', accent: '#B5446E' },
  { id: 'cobre', name: 'Cobre', primary: '#C1662F', accent: '#4A5D3A' },
  { id: 'grafite', name: 'Grafite', primary: '#5B7C99', accent: '#8A6F4E' },
];

/**
 * Deriva o tema completo da barbearia a partir de duas cores.
 *
 * Vira variável CSS no app: trocar a cor repinta tudo em tempo real, sem build.
 */
export function buildTenantTheme(input: { primary: string; accent: string }): TenantTheme {
  const primary = isValidHex(input.primary) ? input.primary : '#C9A227';
  const accent = isValidHex(input.accent) ? input.accent : '#0E5A6B';

  const lightPrimary = ensureContrast(primary, LIGHT_SURFACE);
  const darkPrimary = ensureContrast(primary, DARK_SURFACE);
  const lightAccent = ensureContrast(accent, LIGHT_SURFACE);
  const darkAccent = ensureContrast(accent, DARK_SURFACE);

  return {
    light: {
      primary: lightPrimary,
      primaryForeground: readableForeground(lightPrimary),
      primaryHover: darken(lightPrimary, 0.12),
      accent: lightAccent,
      accentForeground: readableForeground(lightAccent),
    },
    dark: {
      primary: darkPrimary,
      primaryForeground: readableForeground(darkPrimary),
      primaryHover: lighten(darkPrimary, 0.12),
      accent: darkAccent,
      accentForeground: readableForeground(darkAccent),
    },
  };
}
